"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CTA } from "@/components/blog/CTA";
import { CTAGroup } from "@/components/blog/CTAGroup";
import { Callout } from "@/components/blog/Callout";
import { ROICalculator } from "@/components/blog/ROICalculator";
import SourceRef from "@/components/blog/SourceRef";
import { IClosedInlineWidget } from "@/components/common/IClosedInlineWidget";
import { RawHtmlSnippet } from "@/components/common/RawHtmlSnippet";
import { BlogMdxField } from "@/components/common/RuntimeMdx";
import { WistiaVslEmbed } from "@/components/landing/WistiaVslEmbed";
import type { BlogPageComponents, BlogRootProps } from "@/puck/types";
import type { Config } from "@puckeditor/core";
import { createColumnsBlockConfig } from "@/puck/blocks/shared/columnsBlockConfig";
import { studioLinkField } from "@/puck/fields/studioLinkField";
import {
  plainBlogHeadingLabel,
  slugifyBlogHeadingText,
} from "@/utils/stripBlogMarkdown";
import { getBlogGroupingClassName } from "@/utils/blogGroupingStyles";
import {
  getBlogAlignClassName,
  getBlogAlignSlotClassName,
} from "@/utils/blogAlignStyles";
import {
  buildIClosedSchedulerUrl,
  ICLOSED_COLLECTIONS_EVENT_URL,
  ICLOSED_EVENT_TITLE,
} from "@/utils/iclosed";
import {
  normalizeBlogLineTextAlign,
  resolveBlogLineTextPresentation,
} from "@/utils/blogLineTextStyles";
import {
  hasStudioEditableText,
  renderStudioEditableText,
} from "@/utils/blogMdxSource";
import { createStudioImageBlockConfig } from "@/puck/blocks/shared/studioImageBlock";

function renderBlogHeading(
  level: "h2" | "h3" | "h4",
  text: unknown,
  isEditing: boolean,
) {
  const id = slugifyBlogHeadingText(text);
  const tocLabel = plainBlogHeadingLabel(text);
  const className = HEADING_CLASS[level];
  const content = (
    <BlogMdxField value={text} isEditing={isEditing} inline />
  );
  const headingProps = {
    id: id || undefined,
    className,
    ...(tocLabel ? { "data-toc-label": tocLabel } : {}),
    children: content,
  };

  const { children, ...rest } = headingProps;

  if (level === "h2") {
    return (
      <h2 {...rest}>{children}</h2>
    );
  }
  if (level === "h3") {
    return (
      <h3 {...rest}>{children}</h3>
    );
  }
  return (
    <h4 {...rest}>{children}</h4>
  );
}

const HEADING_CLASS: Record<"h2" | "h3" | "h4", string> = {
  h2: "mt-10 mb-4 text-2xl font-bold text-black scroll-mt-20",
  h3: "mt-8 mb-3 text-lg font-bold text-black scroll-mt-20",
  h4: "mt-6 mb-2 text-base font-semibold text-black scroll-mt-20",
};

const CTA_ALIGNMENT_FIELD = {
  type: "select" as const,
  options: [
    { label: "Center", value: "center" },
    { label: "Left", value: "left" },
    { label: "Right", value: "right" },
  ],
};

const LINE_TEXT_MARGIN_FIELD = {
  type: "select" as const,
  label: "Margin (rem)",
  options: [
    { label: "0", value: 0 },
    { label: "1", value: 1 },
    { label: "2", value: 2 },
  ],
};

export {
  DEFAULT_STUDIO_IMAGE_WIDTH as DEFAULT_BLOG_IMAGE_WIDTH,
  DEFAULT_STUDIO_IMAGE_HEIGHT as DEFAULT_BLOG_IMAGE_HEIGHT,
} from "@/utils/studioImageStyles";

function BlogVideoIdPlaceholder() {
  return (
    <div className="flex aspect-video w-full items-center justify-center rounded-[20px] border border-dashed border-black/20 bg-black/3 px-6 text-center">
      <p className="font-monument text-sm text-gail-gray">
        Add a Wistia media ID in this block&apos;s settings to show the video.
      </p>
    </div>
  );
}

function BlogCalendarBlock({
  eyebrow,
  title,
  subtitle,
  iclosedEventUrl,
  iclosedEventTitle,
}: Readonly<{
  eyebrow: string;
  title: string;
  subtitle: string;
  iclosedEventUrl: string;
  iclosedEventTitle: string;
}>) {
  const { t } = useTranslation();
  const schedulerUrl = useMemo(
    () => buildIClosedSchedulerUrl({}, iclosedEventUrl),
    [iclosedEventUrl],
  );

  return (
    <section className="not-prose my-8 w-full rounded-2xl border border-black/10 bg-white p-4 shadow-md md:p-6">
      <div className="mx-auto max-w-2xl text-center">
        {eyebrow ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
            {eyebrow}
          </p>
        ) : null}
        {title ? (
          <h2 className="mt-4 font-monument text-2xl font-bold leading-tight text-black md:text-3xl">
            {title}
          </h2>
        ) : null}
        {subtitle ? (
          <p className="mx-auto mt-4 max-w-xl font-monument text-base leading-relaxed text-gail-gray">
            {subtitle}
          </p>
        ) : null}
      </div>
      <div className="mx-auto mt-6 w-full max-w-3xl">
        <IClosedInlineWidget
          key={schedulerUrl}
          url={schedulerUrl}
          title={iclosedEventTitle}
          fallbackLabel={t("redesign.contactSales.form.schedulingFallback")}
        />
      </div>
    </section>
  );
}

// `ImportStructure` is registered separately in `blogConfig` (it is an
// editor-only helper), so it is intentionally omitted from this block map.
export const blogBlocks: Omit<
  Config<BlogPageComponents, BlogRootProps>["components"],
  "ImportStructure"
> = {
  BlogHeading: {
    label: "Heading",
    fields: {
      level: {
        type: "radio",
        options: [
          { label: "H2", value: "h2" },
          { label: "H3", value: "h3" },
          { label: "H4", value: "h4" },
        ],
      },
      text: { type: "textarea", contentEditable: true },
    },
    defaultProps: {
      level: "h2",
      text: "Section heading",
    },
    render: ({ level, text, puck }) =>
      renderBlogHeading(level, text, puck.isEditing),
  },
  BlogRichText: {
    label: "Paragraph",
    fields: {
      body: { type: "textarea", contentEditable: true },
    },
    defaultProps: {
      body: "Write a paragraph. Line breaks are preserved.",
    },
    render: ({ body, puck }) => (
      <BlogMdxField value={body} isEditing={puck.isEditing} />
    ),
  },
  BlogImage: createStudioImageBlockConfig("Image"),
  BlogCallout: {
    label: "Callout",
    fields: {
      type: {
        type: "radio",
        options: [
          { label: "Info", value: "info" },
          { label: "Warning", value: "warning" },
          { label: "Tip", value: "tip" },
        ],
      },
      body: { type: "textarea", contentEditable: true },
    },
    defaultProps: {
      type: "info",
      body: "Important note for readers.",
    },
    render: ({ type, body, puck }) => (
      <Callout type={type}>
        <BlogMdxField value={body} isEditing={puck.isEditing} />
      </Callout>
    ),
  },
  BlogCTA: {
    label: "CTA button",
    fields: {
      type: {
        type: "radio",
        options: [
          { label: "Primary", value: "primary" },
          { label: "Demo", value: "demo" },
        ],
      },
      link: studioLinkField("Link"),
      text: { type: "text", contentEditable: true },
      align: CTA_ALIGNMENT_FIELD,
    },
    defaultProps: {
      type: "primary",
      link: "/contact-sales",
      text: "Get started",
      align: "center",
    },
    resolveData: ({ props }) => ({
      props: {
        ...props,
        align: props.align ?? "center",
      },
    }),
    render: ({ type, link, text, align }) => (
      <CTA type={type} link={link} text={text} align={align} />
    ),
  },
  BlogCTAGroup: {
    label: "CTA group",
    fields: {
      align: CTA_ALIGNMENT_FIELD,
      items: {
        type: "array",
        min: 1,
        max: 3,
        getItemSummary: (item) => item.text,
        defaultItemProps: {
          type: "primary",
          link: "/contact-sales",
          text: "Get started",
        },
        arrayFields: {
          type: {
            type: "radio",
            options: [
              { label: "Primary", value: "primary" },
              { label: "Demo", value: "demo" },
            ],
          },
          link: studioLinkField("Link"),
          text: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      align: "center",
      items: [
        { type: "primary", link: "/contact-sales", text: "Get started" },
        { type: "demo", link: "/contact-sales", text: "Book a demo" },
      ],
    },
    resolveData: ({ props }) => ({
      props: {
        ...props,
        align: props.align ?? "center",
      },
    }),
    render: ({ align, items }) => (
      <CTAGroup align={align}>
        {items.map((item) => (
          <CTA
            key={`${item.link}-${item.text}`}
            type={item.type}
            link={item.link}
            text={item.text}
          />
        ))}
      </CTAGroup>
    ),
  },
  BlogTwoColumn: createColumnsBlockConfig({
    blockName: "BlogTwoColumn",
    label: "Columns",
    variant: "blog",
    withHeader: false,
  }) as Config<BlogPageComponents, BlogRootProps>["components"]["BlogTwoColumn"],
  BlogSourceRef: {
    label: "Source citation",
    fields: {
      sourceNumber: { type: "number", label: "Source number" },
    },
    defaultProps: {
      sourceNumber: 1,
    },
    render: ({ sourceNumber }) => <SourceRef n={sourceNumber} />,
  },
  BlogROICalculator: {
    label: "ROI calculator",
    fields: {
      intro: {
        type: "textarea",
        contentEditable: true,
        label: "Intro (optional)",
      },
    },
    defaultProps: {
      intro: "",
    },
    render: ({ intro, puck }) => (
      <ROICalculator>
        {intro ? (
          <BlogMdxField value={intro} isEditing={puck.isEditing} />
        ) : null}
      </ROICalculator>
    ),
  },
  BlogGrouping: {
    label: "Grouping",
    fields: {
      variant: {
        type: "select",
        options: [
          { label: "Liquid glass", value: "liquid-glass" },
          { label: "Solid card", value: "solid" },
          { label: "Plain", value: "plain" },
        ],
      },
      content: {
        type: "slot",
        disallow: ["BlogGrouping"],
      },
    },
    defaultProps: {
      variant: "liquid-glass",
      content: [],
    },
    render: ({ variant, content: Content }) => (
      <div
        className={`not-prose flex w-full min-w-0 flex-col gap-2 ${getBlogGroupingClassName(variant)}`}
      >
        <Content className="flex flex-col gap-2" />
      </div>
    ),
  },
  BlogLineText: {
    label: "Line text",
    fields: {
      variant: {
        type: "select",
        options: [
          { label: "Label (uppercase)", value: "label" },
          { label: "Heading", value: "heading" },
          { label: "Custom", value: "custom" },
        ],
      },
      fontSizeRem: {
        type: "number",
        label: "Font size (rem)",
        min: 0,
        step: 0.0625,
      },
      marginRem: LINE_TEXT_MARGIN_FIELD,
      maxWidthRem: {
        type: "number",
        label: "Max width (rem, 0 = none)",
        min: 0,
        step: 0.25,
      },
      align: CTA_ALIGNMENT_FIELD,
      italic: {
        type: "radio",
        options: [
          { label: "Normal", value: false },
          { label: "Italic", value: true },
        ],
      },
      color: {
        type: "radio",
        options: [
          { label: "Black", value: "black" },
          { label: "Blue", value: "blue" },
        ],
      },
      text: { type: "textarea", contentEditable: true },
    },
    defaultProps: {
      variant: "label",
      fontSizeRem: 0.75,
      marginRem: 0,
      maxWidthRem: 0,
      align: "left",
      italic: false,
      color: "black",
      text: "Case study deck",
    },
    resolveData: ({ props }) => ({
      props: {
        ...props,
        italic: props.italic ?? false,
        color: props.color ?? "black",
        maxWidthRem: Math.max(0, Number(props.maxWidthRem) || 0),
        align: normalizeBlogLineTextAlign(props.align),
      },
    }),
    render: ({
      variant,
      fontSizeRem,
      marginRem,
      maxWidthRem,
      align,
      italic,
      color,
      text,
      puck,
    }) => {
      const { className, style } = resolveBlogLineTextPresentation(
        variant,
        fontSizeRem,
        marginRem,
        color,
        italic,
        maxWidthRem,
        align,
      );
      return (
        <p className={className} style={style}>
          <BlogMdxField value={text} isEditing={puck.isEditing} inline />
        </p>
      );
    },
  },
  BlogAlign: {
    label: "Align",
    fields: {
      align: CTA_ALIGNMENT_FIELD,
      direction: {
        type: "radio",
        label: "Layout",
        options: [
          { label: "Vertical", value: "vertical" },
          { label: "Horizontal", value: "horizontal" },
        ],
      },
      content: {
        type: "slot",
        disallow: ["BlogAlign"],
      },
    },
    defaultProps: {
      align: "center",
      direction: "horizontal",
      content: [],
    },
    resolveData: ({ props }) => ({
      props: {
        ...props,
        align: props.align ?? "center",
        direction: props.direction ?? "horizontal",
      },
    }),
    render: ({ align, direction, content: Content }) => (
      <div className={getBlogAlignClassName()}>
        <Content className={getBlogAlignSlotClassName(align, direction)} />
      </div>
    ),
  },
  BlogVideo: {
    label: "Video (Wistia)",
    fields: {
      mediaId: {
        type: "text",
        label: "Wistia media ID (from the video's share/embed URL)",
      },
      autoplay: {
        type: "radio",
        options: [
          { label: "Off", value: false },
          { label: "On muted", value: true },
        ],
      },
      caption: { type: "text", label: "Caption (optional)", contentEditable: true },
    },
    defaultProps: {
      mediaId: "",
      autoplay: false,
      caption: "",
    },
    render: ({ mediaId, autoplay, caption, puck }) => {
      const hasMediaId = (mediaId ?? "").trim().length > 0;
      const hasCaption = hasStudioEditableText(caption);
      const videoContent = hasMediaId ? (
        <WistiaVslEmbed
          mediaId={mediaId.trim()}
          autoplay={puck.isEditing ? false : autoplay}
        />
      ) : puck.isEditing ? (
        <BlogVideoIdPlaceholder />
      ) : null;

      if (!videoContent && !hasCaption) {
        return <></>;
      }

      return (
        <figure className="not-prose my-6 w-full">
          {videoContent}
          {hasCaption ? (
            <figcaption className="mt-3 text-center font-monument text-sm text-gail-gray">
              {renderStudioEditableText(caption)}
            </figcaption>
          ) : null}
        </figure>
      );
    },
  },
  BlogCalendar: {
    label: "Calendar (iClosed)",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      iclosedEventUrl: { type: "text", label: "iClosed event URL" },
      iclosedEventTitle: { type: "text", label: "iClosed event title" },
    },
    defaultProps: {
      eyebrow: "Book a demo",
      title: "See Gail in action",
      subtitle: "Pick a time and our team will tailor the walkthrough to your workflow.",
      iclosedEventUrl: ICLOSED_COLLECTIONS_EVENT_URL,
      iclosedEventTitle: ICLOSED_EVENT_TITLE,
    },
    render: (props) => <BlogCalendarBlock {...props} />,
  },
  BlogSnippet: {
    label: "HTML snippet",
    fields: {
      html: {
        type: "textarea",
        label: "Raw HTML (embed code, iframes, etc.)",
      },
      caption: { type: "text", label: "Caption (optional)", contentEditable: true },
    },
    defaultProps: {
      html: "",
      caption: "",
    },
    render: ({ html, caption }) => (
      <RawHtmlSnippet html={html} caption={caption} className="my-6" />
    ),
  },
};
